import React, { useEffect, useRef, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Minus, Trash2, FileDown, Printer, Image as ImageIcon, Share2, X, Wallet } from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "@/components/CurrencyInput";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Modal completo: saldo do cliente, ajuste, histórico de lançamentos
 * + Botões: Salvar foto, Imprimir, PDF, WhatsApp (relatório financeiro)
 */
export default function CustomerBalance({ customer, store, onClose, onChanged }) {
  const [data, setData] = useState({ balance: 0, entries: [], customer });
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [amount, setAmount] = useState(0);
  const [direction, setDirection] = useState("payment"); // payment | manual
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const reportRef = useRef(null);
  const [showReport, setShowReport] = useState(false);

  const load = async () => {
    const r = await api.get(`/customers/${customer.id}/balance`, { _cache: false });
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customer.id]);

  const submitAdjust = async () => {
    const num = Number(amount || 0);
    if (num <= 0) return toast.error("Informe um valor maior que zero");
    const signed = direction === "payment" ? num : -num;
    setBusy(true);
    try {
      await api.post(`/customers/${customer.id}/balance/adjust`, { amount: signed, note: note || "" });
      toast.success("Saldo atualizado");
      setAdjustOpen(false); setAmount(0); setNote(""); setDirection("payment");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao ajustar");
    } finally { setBusy(false); }
  };

  const removeEntry = async (eid) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    try {
      await api.delete(`/customers/${customer.id}/balance/${eid}`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível excluir"); }
  };

  // ─── Captura do relatório (foto/PDF/print/share) ──────────────────────
  const captureReport = async () => {
    if (!reportRef.current) return null;
    try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch {}
    await new Promise(r => setTimeout(r, 150));
    const htmlEl = document.documentElement;
    const wasDark = htmlEl.classList.contains("dark");
    if (wasDark) htmlEl.classList.remove("dark");
    reportRef.current.classList.add("receipt-capturing");
    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      return await html2canvas(reportRef.current, {
        scale: 3, backgroundColor: "#ffffff", useCORS: true,
        letterRendering: true, logging: false,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight,
      });
    } finally {
      reportRef.current && reportRef.current.classList.remove("receipt-capturing");
      if (wasDark) htmlEl.classList.add("dark");
    }
  };

  const downloadImg = async () => {
    setBusy(true);
    try {
      const c = await captureReport();
      const a = document.createElement("a");
      a.href = c.toDataURL("image/png");
      a.download = `relatorio-${customer.name}.png`;
      a.click();
      toast.success("Imagem salva");
    } catch { toast.error("Erro ao salvar imagem"); }
    finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    setBusy(true);
    try {
      const canvas = await captureReport();
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10, maxWidth = pageWidth - margin * 2, maxHeight = pageHeight - margin * 2;
      const mmPerPx = maxWidth / canvas.width;
      const totalMm = canvas.height * mmPerPx;
      if (totalMm <= maxHeight) {
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, maxWidth, totalMm);
      } else {
        const slicePxHeight = Math.floor(maxHeight / mmPerPx);
        let offset = 0;
        while (offset < canvas.height) {
          const sh = Math.min(slicePxHeight, canvas.height - offset);
          const sc = document.createElement("canvas");
          sc.width = canvas.width; sc.height = sh;
          const ctx = sc.getContext("2d");
          ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, sc.width, sc.height);
          ctx.drawImage(canvas, 0, offset, canvas.width, sh, 0, 0, canvas.width, sh);
          if (offset > 0) pdf.addPage();
          pdf.addImage(sc.toDataURL("image/png"), "PNG", margin, margin, maxWidth, sh * mmPerPx);
          offset += sh;
        }
      }
      pdf.save(`relatorio-${customer.name}.pdf`);
      toast.success("PDF gerado");
    } catch { toast.error("Erro ao gerar PDF"); }
    finally { setBusy(false); }
  };

  const print = async () => {
    if (!reportRef.current) return;
    setBusy(true);
    try {
      const canvas = await captureReport();
      if (canvas.width < 50) { toast.error("Aguarde carregar"); return; }
      const dataUrl = canvas.toDataURL("image/png");
      const iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`<!doctype html><html><head><title>Relatório ${customer.name}</title>
        <style>html,body{margin:0;padding:0;background:#fff;}.page{display:flex;justify-content:center;align-items:flex-start;}img{display:block;width:100%;height:auto;}@page{size:A4;margin:6mm;}</style></head>
        <body><div class="page"><img src="${dataUrl}"/></div></body></html>`);
      doc.close();
      const cleanup = () => { try { document.body.removeChild(iframe); } catch {} };
      const triggerPrint = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch {}
        setTimeout(cleanup, 1500);
      };
      const img = doc.querySelector("img");
      if (img && !img.complete) img.onload = () => setTimeout(triggerPrint, 100);
      else setTimeout(triggerPrint, 200);
    } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const canvas = await captureReport();
      const blob = await new Promise(r => canvas.toBlob(r, "image/png"));
      const file = new File([blob], `relatorio-${customer.name}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Relatório Financeiro" });
      } else {
        // fallback: link WhatsApp com texto
        const phone = (customer.phone || "").replace(/\D/g, "");
        const wa = phone.length >= 11 ? "55" + phone : phone;
        window.open(`https://wa.me/${wa}?text=${encodeURIComponent("Relatório Financeiro - " + customer.name + "\\nSaldo: " + brl(data.balance))}`, "_blank");
      }
    } catch { toast.error("Compartilhamento não disponível"); }
    finally { setBusy(false); }
  };

  // ─── Cobrança via WhatsApp e link público ────────────────────────────
  const phoneDigits = (customer.phone || "").replace(/\D/g, "");
  const customerWaPrefix = phoneDigits.length >= 11 ? `55${phoneDigits}` : phoneDigits;
  const [chargeAmount, setChargeAmount] = useState(0);
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  // Sincroniza estado de share-link a partir de data.customer
  useEffect(() => {
    const cu = data.customer || customer;
    if (cu?.share_token) {
      setShareLink(`${window.location.origin}/extrato/${cu.share_token}`);
      setShareEnabled(!!cu.share_enabled);
    } else {
      setShareLink(""); setShareEnabled(false);
    }
  }, [data.customer, customer]);

  const sendChargeWhatsApp = (mode /* "specific" | "total" */) => {
    if (!customerWaPrefix) {
      toast.error("Cliente sem telefone cadastrado");
      return;
    }
    let amount = 0;
    let prefix = "";
    if (mode === "total") {
      amount = Math.abs(Math.min(0, Number(data.balance || 0)));
      if (amount <= 0) { toast.info("Cliente não tem saldo devedor"); return; }
      prefix = "Olá! Segue o link para pagamento total do seu saldo devedor:";
    } else {
      amount = Math.max(0, Number(chargeAmount || 0));
      if (amount <= 0) { toast.error("Informe um valor maior que zero"); return; }
      prefix = "Olá! Valor disponível para pagamento, segue o link:";
    }
    // Garante que o link público está ativo. Se ainda não existir, cria agora.
    const proceed = async () => {
      let token = (data.customer || customer)?.share_token;
      let enabled = (data.customer || customer)?.share_enabled;
      if (!token || !enabled) {
        try {
          const r = await api.post(`/customers/${customer.id}/share-link`);
          token = r.data?.share_token;
          enabled = !!r.data?.share_enabled;
          await load();
        } catch {
          toast.error("Não foi possível gerar o link público");
          return;
        }
      }
      const link = `${window.location.origin}/extrato/${token}?valor=${amount.toFixed(2)}`;
      const msg = `${prefix}\n${link}\n\nValor: ${brl(amount)}`;
      window.open(`https://wa.me/${customerWaPrefix}?text=${encodeURIComponent(msg)}`, "_blank");
      setChargeDialogOpen(false);
      setChargeAmount(0);
    };
    proceed();
  };

  const toggleShareLink = async () => {
    setShareBusy(true);
    try {
      if (shareEnabled) {
        await api.delete(`/customers/${customer.id}/share-link`);
        toast.success("Link desativado");
      } else {
        await api.post(`/customers/${customer.id}/share-link`);
        toast.success("Link ativado");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar link");
    } finally { setShareBusy(false); }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar — copie manualmente");
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";
  const balanceColor = data.balance < 0 ? "text-red-600" : data.balance > 0 ? "text-emerald-600" : "text-neutral-600";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 no-print" data-testid="customer-balance-modal">
      <div className="bg-white max-w-lg w-full max-h-[92vh] overflow-y-auto rounded-md">
        <div className="p-5 border-b border-[#E5E5E5] flex justify-between items-center bg-white">
          <div>
            <p className="label-tiny">Cliente · Saldo</p>
            <h2 className="font-display text-xl mt-0.5">{customer.name}</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Saldo + ajuste */}
          <div className="flex items-center justify-between bg-neutral-50 border border-[#E5E5E5] rounded-md p-4">
            <div>
              <div className="label-tiny">Saldo atual</div>
              <div className={`font-display text-3xl mt-1 ${balanceColor}`} data-testid="customer-balance-amount">{brl(data.balance)}</div>
            </div>
            <Button onClick={()=>{ setDirection("payment"); setAdjustOpen(true); }} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="adjust-balance-button">
              <Wallet size={14} className="mr-1"/> Ajustar saldo
            </Button>
          </div>

          {/* Cobrança via WhatsApp */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              className="bg-green-500 hover:bg-green-600 text-white"
              onClick={()=>setChargeDialogOpen(true)}
              data-testid="charge-specific-btn"
            >
              Cobrar valor específico
            </Button>
            <Button
              size="sm"
              className="bg-green-700 hover:bg-green-800 text-white"
              onClick={()=>sendChargeWhatsApp("total")}
              disabled={Number(data.balance) >= 0}
              title={Number(data.balance) >= 0 ? "Cliente sem saldo devedor" : "Enviar cobrança do saldo total"}
              data-testid="charge-total-btn"
            >
              Cobrar saldo total {Number(data.balance) < 0 && `(${brl(Math.abs(data.balance))})`}
            </Button>
          </div>

          {/* Link público de extrato */}
          <div className="bg-neutral-50 border border-[#E5E5E5] rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-xs font-medium">Link público do extrato</div>
                <div className="text-[10px] text-neutral-500">
                  {shareEnabled ? "Ativo — qualquer pessoa com o link consegue ver o extrato" : "Desativado"}
                </div>
              </div>
              <Button
                size="sm"
                variant={shareEnabled ? "outline" : "default"}
                className={shareEnabled ? "border-red-300 text-red-600 hover:bg-red-50" : "bg-[#0A0A0A] hover:bg-black text-white"}
                onClick={toggleShareLink}
                disabled={shareBusy}
                data-testid="toggle-share-link-btn"
              >
                {shareBusy ? "…" : (shareEnabled ? "Desativar" : "Ativar")}
              </Button>
            </div>
            {shareEnabled && shareLink && (
              <div className="flex items-center gap-2">
                <Input value={shareLink} readOnly className="h-8 text-xs bg-white" data-testid="share-link-input"/>
                <Button size="sm" variant="outline" onClick={copyShareLink} data-testid="share-link-copy-btn">
                  Copiar
                </Button>
              </div>
            )}
          </div>

          {/* Histórico */}
          <div>
            <p className="label-tiny mb-2">Histórico de lançamentos</p>
            {data.entries.length === 0 ? (
              <p className="text-xs text-neutral-500 py-6 text-center border border-dashed border-[#E5E5E5] rounded-md">Sem lançamentos ainda.</p>
            ) : (
              <div className="space-y-1 border border-[#E5E5E5] rounded-md divide-y divide-[#E5E5E5]">
                {data.entries.map(e => (
                  <div key={e.id} className="p-3 text-sm flex items-start justify-between gap-3" data-testid={`balance-entry-${e.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className={`font-display ${e.amount < 0 ? "text-red-600" : "text-emerald-600"}`}>
                        {e.amount < 0 ? "−" : "+"}{brl(Math.abs(e.amount))}
                      </div>
                      <div className="text-xs text-neutral-700 break-words">{e.description}</div>
                      {e.note && <div className="text-[11px] text-neutral-500 mt-0.5 italic">Obs.: {e.note}</div>}
                      <div className="text-[10px] text-neutral-400 mt-0.5">{fmtDate(e.created_at)} · {e.created_by || "—"}</div>
                    </div>
                    {e.type !== "charge" && (
                      <button onClick={()=>removeEntry(e.id)} className="text-neutral-400 hover:text-red-600 p-1" title="Excluir lançamento">
                        <Trash2 size={13}/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ações relatório */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={downloadImg} disabled={busy} data-testid="report-img-button"><ImageIcon size={12} className="mr-1"/> Foto</Button>
            <Button variant="outline" size="sm" onClick={print} disabled={busy} data-testid="report-print-button"><Printer size={12} className="mr-1"/> Imprimir</Button>
            <Button variant="outline" size="sm" onClick={downloadPDF} disabled={busy} data-testid="report-pdf-button"><FileDown size={12} className="mr-1"/> PDF</Button>
            <Button variant="outline" size="sm" onClick={share} disabled={busy} data-testid="report-share-button"><Share2 size={12} className="mr-1"/> WhatsApp</Button>
          </div>
          <p className="text-[10px] text-neutral-400 text-center">Os botões geram um relatório financeiro do cliente.</p>
        </div>
      </div>

      {/* Relatório (renderizado fora da tela para captura) */}
      <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}>
        <div ref={reportRef} className="receipt p-8" style={{ width: 480, background: "#FFFFFF", color: "#0A0A0A", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto" }}>
          <div className="text-center mb-4">
            {store.logo && <img src={store.logo} alt="" style={{ width: 56, height: 56, borderRadius: 999, objectFit: "cover", margin: "0 auto" }}/>}
            <div className="font-display text-xl mt-2">{store.name || "Loja"}</div>
            {store.address && <div className="text-[10px] text-neutral-500">{store.address}</div>}
            {store.phone && <div className="text-[10px] text-neutral-500">{store.phone}</div>}
            {store.cnpj && <div className="text-[10px] text-neutral-500">CNPJ: {store.cnpj}</div>}
          </div>
          <hr className="my-3 border-[#E5E5E5]"/>
          <div className="text-[11px] mb-3">
            <div className="font-semibold uppercase tracking-wider text-[10px] text-neutral-500 mb-1">Cliente</div>
            <div className="font-display text-base">{customer.name}</div>
            {customer.phone && <div>Telefone: {customer.phone}</div>}
            {customer.cpf && <div>CPF: {customer.cpf}</div>}
            {customer.address && <div>Endereço: {customer.address}</div>}
          </div>
          <hr className="my-3 border-[#E5E5E5]"/>
          <div className="font-semibold uppercase tracking-wider text-[10px] text-neutral-500 mb-2">Relatório financeiro</div>
          {data.entries.length === 0 ? (
            <div className="text-xs text-neutral-500">Sem lançamentos.</div>
          ) : (
            <div className="space-y-1.5">
              {[...data.entries].reverse().map(e => (
                <div key={e.id} className="flex justify-between items-start text-[11px] gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="break-words">
                      <span className={e.amount < 0 ? "text-red-600 font-medium" : "text-emerald-700 font-medium"}>
                        {e.amount < 0 ? "−" : "+"}{brl(Math.abs(e.amount))}
                      </span>{" "}
                      <span>{e.description}</span>
                    </div>
                    {e.note && <div className="text-[10px] text-neutral-500 italic">(Obs.: {e.note})</div>}
                  </div>
                  <div className="text-[9px] text-neutral-400 whitespace-nowrap">{fmtDate(e.created_at)}</div>
                </div>
              ))}
            </div>
          )}
          <hr className="my-3 border-[#E5E5E5]"/>
          <div className="flex justify-between items-baseline">
            <div className="font-semibold uppercase tracking-wider text-[11px]">Saldo</div>
            <div className={`font-display text-xl ${balanceColor}`}>{brl(data.balance)}</div>
          </div>
          <div className="text-[9px] text-neutral-400 mt-3 text-center">Documento gerado em {new Date().toLocaleString("pt-BR")}</div>
        </div>
      </div>

      {/* Dialog cobrar valor específico */}
      <Dialog open={chargeDialogOpen} onOpenChange={setChargeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cobrar valor específico</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Valor a cobrar</Label>
              <CurrencyInput value={chargeAmount} onChange={(v)=>setChargeAmount(v)} data-testid="charge-amount-input"/>
            </div>
            <p className="text-[11px] text-neutral-500">
              O cliente receberá uma mensagem no WhatsApp com o link público do extrato pré-preenchido com este valor.
              Ele pode pagar via PIX e enviar o comprovante.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setChargeDialogOpen(false)}>Cancelar</Button>
            <Button onClick={()=>sendChargeWhatsApp("specific")} className="bg-green-500 hover:bg-green-600 text-white" data-testid="charge-send-wa-btn">
              Enviar no WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ajustar saldo */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ajustar saldo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={()=>setDirection("payment")}
                className={`px-3 py-2 rounded-md text-xs border transition ${direction === "payment" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-[#E5E5E5] text-neutral-600 hover:border-neutral-300"}`}
                data-testid="direction-payment"
              ><Plus size={11} className="inline mr-1"/> Pagamento recebido</button>
              <button
                type="button"
                onClick={()=>setDirection("manual")}
                className={`px-3 py-2 rounded-md text-xs border transition ${direction === "manual" ? "bg-red-50 border-red-300 text-red-700" : "border-[#E5E5E5] text-neutral-600 hover:border-neutral-300"}`}
                data-testid="direction-manual"
              ><Minus size={11} className="inline mr-1"/> Débito manual</button>
            </div>
            <div>
              <Label>Valor</Label>
              <CurrencyInput value={amount} onChange={setAmount} data-testid="balance-amount-input"/>
            </div>
            <div>
              <Label>Observação (opcional)</Label>
              <Textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="Ex.: Pagou em dinheiro na data combinada" rows={2} data-testid="balance-note-input"/>
            </div>
            <p className="text-[11px] text-neutral-500">
              {direction === "payment"
                ? "O valor será SOMADO ao saldo (registra um pagamento recebido)."
                : "O valor será SUBTRAÍDO do saldo (registra um débito manual)."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAdjustOpen(false)}>Cancelar</Button>
            <Button onClick={submitAdjust} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="balance-submit">
              {busy ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
